import type { FluxErrorEntityErrorCodes } from '../types.js';

export const GenericErrorCodes = {
  BadRequest: {
    code: 'BAD_REQUEST',
    explanation: `Esse erro é retornado quando o **payload da requisição não está em conformidade** com o contrato da API, seja por violação do schema GraphQL ou falha nas validações REST.

**Causas comuns:**
- Campos com **tipagem incorreta** (ex: string enviada onde se espera número)
- **Campos obrigatórios ausentes** no payload
- **Formato de dados inválido** (datas, emails, CPF/CNPJ, URLs, etc)
- Valores fora do **range permitido** ou que violam regras de validação customizadas

**Como resolver:**
Verifique o schema da API e garanta que todos os campos estão sendo enviados com os tipos e formatos corretos. Consulte a documentação do endpoint para detalhes sobre validações específicas.`,
    localizations: {},
    message:
      'Não foi possível processar sua solicitação devido a dados inválidos. Por favor, verifique as informações preenchidas e tente novamente.',
  },
  Forbidden: {
    code: 'FORBIDDEN',
    explanation: `Esse erro ocorre quando o usuário **está autenticado**, mas **não possui as permissões necessárias** (roles, scopes ou policies) para acessar o recurso solicitado.

**Diferença entre \`FORBIDDEN\` e \`UNAUTHORIZED\`:**
- \`UNAUTHORIZED\` (401): Usuário não autenticado ou token inválido
- \`FORBIDDEN\` (403): Usuário autenticado, mas sem permissão

**Como diagnosticar:**
Verifique as permissões do usuário no \`AuthContext\` ou no sistema de controle de acesso. Confirme se o usuário possui a role/scope necessária para a operação (ex: \`admin\`, \`orders:write\`, etc).`,
    localizations: {},
    message:
      'Você não tem permissão para acessar este recurso. Entre em contato com seu administrador caso acredite que isso seja um erro.',
    supportingDocs: [
      { name: 'Permissões e Cargos', path: '/wiki/docs/roles-and-permissions/roles' },
    ],
  },
  InternalServerError: {
    code: 'INTERNAL_SERVER_ERROR',
    explanation: `Esse erro indica uma **exceção não tratada ou falha inesperada** no servidor durante o processamento da requisição.

**Causas comuns:**
- **Exceções não capturadas** (ex: \`NullPointerException\`, \`TypeError\`)
- **Falhas de conexão** com banco de dados, cache ou serviços externos
- **Bugs na lógica de negócio** que não foram previstos
- **Timeouts internos** em operações assíncronas
- **Recursos esgotados** (memória, conexões de pool, etc)

**Como diagnosticar:**
Verifique os **logs do servidor** para encontrar o stacktrace completo. Procure por exceções não tratadas e analise o contexto da requisição que causou o erro.`,
    localizations: {},
    message:
      'Ocorreu um erro inesperado em nossos servidores. Nossa equipe já foi notificada. Por favor, tente novamente mais tarde.',
  },
  NotFound: {
    code: 'NOT_FOUND',
    explanation: `Esse erro é retornado quando o **recurso solicitado não foi encontrado** no banco de dados ou sistema de armazenamento.

**Cenários comuns:**
- O **ID fornecido está incorreto** ou não existe
- O recurso foi **deletado** (soft delete ou hard delete)
- Em sistemas **multi-tenant**, o usuário não tem acesso ao recurso de outro tenant
- O recurso existe, mas está em um **estado que o torna invisível** (ex: arquivado, inativo)

**Como diagnosticar:**
Verifique se o ID está correto e se o recurso realmente existe no banco de dados. Em casos de multi-tenancy, confirme que o \`tenant_id\` está sendo aplicado corretamente nas queries.`,
    localizations: {},
    message: 'O item que você está procurando não foi encontrado ou não está mais disponível.',
  },
  Unauthorized: {
    code: 'UNAUTHORIZED',
    explanation: `Esse erro indica que o usuário **não está autenticado** ou que as **credenciais fornecidas são inválidas**.

**Causas comuns:**
- **Token JWT expirado** ou com assinatura inválida
- **Header \`Authorization\` ausente** ou malformado
- **Credenciais inválidas** (usuário/senha incorretos)
- **Token revogado** ou invalidado no servidor
- **Sessão expirada** por inatividade

**Como diagnosticar:**
Verifique se o header \`Authorization\` está sendo enviado corretamente (formato: \`Bearer <token>\`). Valide se o token JWT não está expirado e se a assinatura é válida. Confirme que o usuário possui uma sessão ativa.`,
    localizations: {},
    message: 'Sua sessão expirou ou é inválida. Por favor, faça login novamente para continuar.',
  },
  Unknown: {
    code: 'UNKNOWN',
    explanation: `Erro genérico utilizado como **fallback** quando a causa raiz do problema não pode ser determinada ou categorizada.

**Quando usar:**
Este erro deve ser usado apenas quando não há informações suficientes para classificar o erro em uma categoria específica. Em produção, a ocorrência frequente deste erro indica que **novos tipos de erro precisam ser mapeados**.

**Como tratar:**
Trate este erro da mesma forma que um \`INTERNAL_SERVER_ERROR\`. Investigue os logs para identificar a causa e, se possível, crie um código de erro mais específico.`,
    localizations: {},
    message: 'Algo deu errado. Por favor, atualize a página e tente novamente.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'generic'>;
